import React, { useEffect, useState, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/pro-regular-svg-icons";
import LoginPage from "./pages/auth/LoginPage";

import TopEditPage from "./pages/website/top/TopEditPage";
import AboutEditPage from "./pages/website/about/AboutEditPage";
import PriceEditPage from "./pages/website/price/PriceEditPage";
import StudioEditPage from "./pages/website/studio/StudioEditPage";
import LessonEditPage from "./pages/website/lesson/LessonEditPage";
import InstructorEditPage from "./pages/website/instructor/InstructorEditPage";
import QaEditPage from "./pages/website/qa/QaEditPage";
import NewsEditPage from "./pages/website/news/NewsEditPage";
import TrialListPage from "./pages/management/TrialList";
import MetaAdsPage from "./pages/management/MetaAdsPage";
import ReviewEditPage from "./pages/website/review/ReviewEditPage";
import NewVisitorEditPage from "./pages/website/newvisitor/NewVisitorEditPage";
import CampaignEditPage from "./pages/website/campaign/CampaignEditPage";

import useBodyClass from "./hooks/useBodyClass";
import GradientCanvas from "./components/GradientCanvas.js";

function AppContent() {
  // ログイン状態
  const { isLoggedIn, isSuperuser, username, logout } = useAuth();
  const location = useLocation();

  // サイドバーの開閉状態
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // useBodyClassを最上位で呼び出し
  useBodyClass();

  // 動的ID管理
  const [firstStudioId, setFirstStudioId] = useState(1);

  // 最小IDを取得する関数
  const fetchFirstIds = async () => {
    if (!isLoggedIn) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL;

      // スタジオの最小IDを取得
      const studioResponse = await fetch(`${apiUrl}/api/get_first_id/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ type: "studio" }),
      });
      if (studioResponse.ok) {
        const studioData = await studioResponse.json();
        if (studioData.id) {
          setFirstStudioId(studioData.id);
        }
      }
    } catch (error) {
      console.error("IDの取得に失敗しました:", error);
    }
  };

  // ログイン状態が変わったら最小IDを取得
  useEffect(() => {
    fetchFirstIds();
  }, [isLoggedIn]);

  // 画面（メモ化されたコンポーネント）
  const RenderPage = useCallback(() => {
    if (!isLoggedIn) {
      return <Navigate to="/login" replace />;
    }

    return (
      <>
        <main>
          <Routes>
            {/* website */}
            <Route path="/" element={<TopEditPage />} />
            <Route path="/about" element={<AboutEditPage />} />
            <Route path="/price" element={<PriceEditPage />} />
            <Route path="/studio/:studioId" element={<StudioEditPage />} />
            <Route path="/lesson/:studioId" element={<LessonEditPage />} />
            <Route path="/instructor" element={<InstructorEditPage />} />
            <Route path="/news" element={<NewsEditPage />} />
            <Route path="/news/:newsId" element={<NewsEditPage />} />
            <Route path="/qa" element={<QaEditPage />} />
            <Route path="/review" element={<ReviewEditPage />} />
            <Route path="/newvisitor" element={<NewVisitorEditPage />} />
            <Route path="/campaign" element={<CampaignEditPage />} />
            {/* management */}
            <Route path="/trial" element={<TrialListPage />} />
            <Route path="/meta-ads" element={<MetaAdsPage />} />
          </Routes>
        </main>
      </>
    );
  }, [isLoggedIn, firstStudioId]);

  // 認証関連のページかどうかを判定
  const isAuthPage =
    ["/login", "/forgot-password"].includes(location.pathname) ||
    location.pathname.startsWith("/reset-password");

  return (
    <div className="wrapper">
      {!isAuthPage && (
        <>
          <nav className={`sidebar ${isSidebarOpen ? "sidebar-open" : ""}`}>
            <h1>RUDO FACTORY</h1>
            <ul>
              <Link to="/" onClick={() => setIsSidebarOpen(false)}>
                <li>Top</li>
              </Link>

              <Link to="/about" onClick={() => setIsSidebarOpen(false)}>
                <li>About</li>
              </Link>
              <Link to="/price" onClick={() => setIsSidebarOpen(false)}>
                <li>Price</li>
              </Link>
              <Link
                to={`/studio/${firstStudioId}`}
                onClick={() => setIsSidebarOpen(false)}
              >
                <li>Studio</li>
              </Link>
              <Link
                to={`/lesson/${firstStudioId}`}
                onClick={() => setIsSidebarOpen(false)}
              >
                <li>Lesson</li>
              </Link>
              <Link to="/instructor" onClick={() => setIsSidebarOpen(false)}>
                <li>Instructor</li>
              </Link>
              <Link to="/news" onClick={() => setIsSidebarOpen(false)}>
                <li>News</li>
              </Link>
              <Link to="/qa" onClick={() => setIsSidebarOpen(false)}>
                <li>Q&A</li>
              </Link>
              <Link to="/review" onClick={() => setIsSidebarOpen(false)}>
                <li>Review</li>
              </Link>
              <Link to="/newvisitor" onClick={() => setIsSidebarOpen(false)}>
                <li>New Visitor</li>
              </Link>
              <Link to="/campaign" onClick={() => setIsSidebarOpen(false)}>
                <li>Campaign</li>
              </Link>
              <Link to="/trial" onClick={() => setIsSidebarOpen(false)}>
                <li>Trial List</li>
              </Link>
              <Link to="/meta-ads" onClick={() => setIsSidebarOpen(false)}>
                <li>Meta Ads</li>
              </Link>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  logout();
                  setIsSidebarOpen(false);
                }}
              >
                <li>Logout</li>
              </a>
            </ul>
          </nav>
          <div className="headder">
            <div
              className="hamburger-menu"
              onClick={(e) => {
                e.preventDefault();
                setIsSidebarOpen(!isSidebarOpen);
              }}
            >
              <FontAwesomeIcon icon={faBars} className="menu-btn-icon" />
            </div>
            <p className="username">{username || "admin"}</p>
          </div>
          {isSidebarOpen && (
            <div
              className="sidebar-overlay"
              onClick={() => setIsSidebarOpen(false)}
            ></div>
          )}
        </>
      )}
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<RenderPage />} />
      </Routes>
      <GradientCanvas />
    </div>
  );
}

// アプリケーションコンポーネント
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
